/**
 * useViewSnap - 視窗 Snap 功能
 * 處理視窗對齊到邊緣的偵測、預覽和應用
 */

import type { CSSProperties } from 'vue';

/**
 * 座標位置介面
 */
export interface Position {
    x: number;
    y: number;
}

/**
 * 尺寸大小介面
 */
export interface Size {
    width: number;
    height: number;
}

/**
 * Snap 區域類型
 */
export type SnapZone = 'maximize' | 'left' | 'right' | null;

/**
 * Snap 前的配置介面
 */
export interface PreSnapConfig {
    position: Position;
    size: Size;
}

/**
 * 視圖物件介面（Snap 功能所需的屬性）
 */
export interface SnapView {
    position: Position;
    size: Size;
    isSnapped?: boolean;
    snapZone?: SnapZone;
    preSnapConfig?: PreSnapConfig | null;
    isMaximized?: boolean;
}

/**
 * 導航列配置介面
 */
export interface NavConfig {
    /** 是否為水平導航列 */
    isHorizontal: boolean;
    /** 導航列左側偏移量 */
    navLeft: number;
    /** 導航列頂部偏移量 */
    navTop: number;
}

/**
 * Snap 佈局結果介面
 */
export interface SnapLayout {
    position: Position;
    size: Size;
}

/**
 * Snap 預覽樣式介面
 */
export interface SnapPreviewStyle extends Pick<CSSProperties, 'position' | 'left' | 'top' | 'right' | 'width' | 'height'> {
    position: 'fixed';
}

/**
 * 取得導航列配置
 * @returns 導航列配置
 */
export function getNavConfig(): NavConfig {
    // 預設為水平模式，只有明確設為 'vertical' 才是垂直模式
    const isHorizontal = localStorage.getItem('navOrientation') !== 'vertical';
    return {
        isHorizontal,
        navLeft: isHorizontal ? 0 : 60,   // 垂直模式左側導航 60px
        navTop: isHorizontal ? 60 : 0     // 水平模式頂部導航 60px
    };
}

/**
 * 偵測滑鼠位置是否在 Snap 區域
 * @param clientX - 滑鼠 X 座標
 * @param clientY - 滑鼠 Y 座標
 * @returns Snap 區域
 */
export function detectSnapZone(clientX: number, clientY: number): SnapZone {
    const threshold = 20;  // 邊緣觸發距離
    const maxWidth = window.innerWidth;

    // 檢測是否為水平選單模式
    const isHorizontalNav = localStorage.getItem('navOrientation') !== 'vertical';
    // 水平模式：拉到選單區域 (Y < 80) 觸發最大化
    // 垂直模式：拉到畫面頂部 (Y < 20) 觸發最大化
    const maximizeThreshold = isHorizontalNav ? 80 : threshold;
    // 垂直模式：拉到選單區域 (X < 80) 觸發左側 snap
    // 水平模式：拉到畫面左邊 (X < 20) 觸發左側 snap
    const leftThreshold = isHorizontalNav ? threshold : 80;

    // 優先偵測上方（最大化）
    if (clientY <= maximizeThreshold) {
        return 'maximize';
    }
    // 左右邊緣
    if (clientX <= leftThreshold) {
        return 'left';
    } else if (clientX >= maxWidth - threshold) {
        return 'right';
    }
    return null;
}

/**
 * 取得 Snap 預覽區域的樣式
 * @param zone - Snap 區域
 * @returns CSS 樣式物件
 */
export function getSnapPreviewStyle(zone: SnapZone): SnapPreviewStyle | null {
    if (!zone) return null;

    const { navLeft, navTop } = getNavConfig();
    const gap = 10;
    const halfWidth = (window.innerWidth - navLeft - gap * 3) / 2;

    if (zone === 'maximize') {
        return {
            position: 'fixed',
            left: `${navLeft + gap}px`,
            top: `${navTop + gap}px`,
            right: `${gap}px`,
            height: `calc(100vh - ${navTop + gap * 2}px)`
        };
    } else if (zone === 'left') {
        return {
            position: 'fixed',
            left: `${navLeft + gap}px`,
            top: `${navTop + gap}px`,
            width: `${halfWidth}px`,
            height: `calc(100vh - ${navTop + gap * 2}px)`
        };
    } else if (zone === 'right') {
        return {
            position: 'fixed',
            right: `${gap}px`,
            top: `${navTop + gap}px`,
            width: `${halfWidth}px`,
            height: `calc(100vh - ${navTop + gap * 2}px)`
        };
    }
    return null;
}

/**
 * 計算 Snap 後的視窗位置和大小
 * @param zone - Snap 區域
 * @returns Snap 佈局
 */
export function calculateSnapLayout(zone: SnapZone): SnapLayout | null {
    const { navLeft, navTop } = getNavConfig();
    const gap = 10;
    const maxWidth = window.innerWidth;
    const maxHeight = window.innerHeight;
    const halfWidth = (maxWidth - navLeft - gap * 3) / 2;
    const availableHeight = maxHeight - navTop - gap * 2;
    const availableWidth = maxWidth - navLeft - gap * 2;

    if (zone === 'maximize') {
        return {
            position: { x: navLeft + gap, y: navTop + gap },
            size: { width: availableWidth, height: availableHeight }
        };
    } else if (zone === 'left') {
        return {
            position: { x: navLeft + gap, y: navTop + gap },
            size: { width: halfWidth, height: availableHeight }
        };
    } else if (zone === 'right') {
        return {
            position: { x: navLeft + gap * 2 + halfWidth, y: navTop + gap },
            size: { width: halfWidth, height: availableHeight }
        };
    }
    return null;
}

/**
 * 應用 Snap 到視窗
 * @param view - 視窗物件
 * @param zone - Snap 區域
 */
export function applySnapToView(view: SnapView | null, zone: SnapZone): void {
    if (!view || !zone) return;

    // 儲存 Snap 前的狀態（用於還原）
    if (!view.isSnapped) {
        view.preSnapConfig = {
            position: { ...view.position },
            size: { ...view.size }
        };
    }

    const layout = calculateSnapLayout(zone);
    if (layout) {
        view.position = layout.position;
        view.size = layout.size;
    }

    view.isSnapped = true;
    view.snapZone = zone;  // 儲存 snap 方向: 'left', 'right', 'maximize'
}

/**
 * 從 Snap 狀態還原視窗
 * @param view - 視窗物件
 * @returns 是否成功還原
 */
export function restoreFromSnap(view: SnapView | null): boolean {
    if (!view || !view.isSnapped || !view.preSnapConfig) {
        return false;
    }

    view.position = { ...view.preSnapConfig.position };
    view.size = { ...view.preSnapConfig.size };

    // 確保還原後的位置在畫面內
    const { navTop } = getNavConfig();
    const isHorizontalNav = localStorage.getItem('navOrientation') !== 'vertical';
    const minY = isHorizontalNav ? 60 : 0;
    const maxY = window.innerHeight - 50;
    const minX = -(view.size.width - 100);
    const maxX = window.innerWidth - 100;

    view.position.x = Math.max(minX, Math.min(view.position.x, maxX));
    view.position.y = Math.max(minY, Math.min(view.position.y, maxY));

    view.isSnapped = false;
    view.snapZone = null;
    view.preSnapConfig = null;

    return true;
}

export default {
    getNavConfig,
    detectSnapZone,
    getSnapPreviewStyle,
    calculateSnapLayout,
    applySnapToView,
    restoreFromSnap
};
